import React from 'react';
import { motion } from 'framer-motion';
import { Award, Target, Sparkles, Zap } from 'lucide-react';
import { ProcessedOfficer, formatHm } from '../lib/officerUtils';
interface AwardsStripProps {
  officers: ProcessedOfficer[];
  onSelect: (name: string) => void;
}
interface AwardDef {
  icon: any;
  eyebrow: string;
  title: string;
  recipient: string;
  metric: string;
}
export function AwardsStrip({
  officers,
  onSelect
}: AwardsStripProps) {
  const valid = officers.filter(o => o.loginMin > 0);
  // Most submissions — the most concrete production metric
  const topProducer = [...valid].sort((a, b) => b.submissions - a.submissions)[0];
  // Most leads converted today
  const leadMagnet = [...valid].sort((a, b) => b.leadsConverted - a.leadsConverted)[0];
  // Highest unified production score
  const bestScore = [...valid].sort((a, b) => b.productionScore - a.productionScore)[0];
  // Lowest wrap-up ratio (efficiency)
  const tightestWrap = [...valid].filter(o => o.wrapUpMin > 0).sort((a, b) => a.wrapUpRatio - b.wrapUpRatio)[0];
  const awards: AwardDef[] = [{
    icon: Award,
    eyebrow: 'Top Producer',
    title: topProducer?.name ?? '—',
    recipient: topProducer?.name ?? '',
    metric: `${topProducer?.submissions ?? 0} submissions today`
  }, {
    icon: Target,
    eyebrow: 'Lead Magnet',
    title: leadMagnet?.name ?? '—',
    recipient: leadMagnet?.name ?? '',
    metric: `${leadMagnet?.leadsConverted ?? 0} leads converted`
  }, {
    icon: Sparkles,
    eyebrow: 'Best Production',
    title: bestScore?.name ?? '—',
    recipient: bestScore?.name ?? '',
    metric: `${bestScore?.productionScore.toFixed(1)} production score`
  }, {
    icon: Zap,
    eyebrow: 'Tightest Wrap',
    title: tightestWrap?.name ?? '—',
    recipient: tightestWrap?.name ?? '',
    metric: `${((tightestWrap?.wrapUpRatio ?? 0) * 100).toFixed(1)}% of day in wrap-up`
  }];
  return <section className="w-full px-8 lg:px-12 xl:px-16 pb-14" data-id="element-13">
      <div className="flex items-baseline justify-between mb-6" data-id="element-14">
        <h2 className="font-serif font-bold text-2xl text-primary" data-id="element-15">
          Today's recognitions
        </h2>
        <span className="text-xs uppercase tracking-[0.22em] text-primary/50 font-bold" data-id="element-16">
          Four standouts
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink-10 border border-ink-10" data-id="element-17">
        {awards.map((a, i) => <AwardCard key={a.eyebrow} award={a} index={i} onClick={() => a.recipient && onSelect(a.recipient)} data-id="element-18" />)}
      </div>
    </section>;
}
function AwardCard({
  award,
  index,
  onClick
}: {
  award: AwardDef;
  index: number;
  onClick: () => void;
}) {
  const Icon = award.icon;
  return <motion.button type="button" onClick={onClick} initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    delay: 0.25 + index * 0.08
  }} whileHover={{
    y: -2
  }} className="group relative bg-bone text-left p-7 cursor-pointer transition-colors hover:bg-bone-2/40" data-id="element-19">
      <div className="flex items-start justify-between mb-10" data-id="element-20">
        <div className="flex items-center gap-2" data-id="element-21">
          <span className="w-1.5 h-1.5 rounded-full bg-signal" data-id="element-22" />
          <span className="text-[10px] uppercase tracking-[0.28em] text-ink-50 font-medium" data-id="element-23">
            {award.eyebrow}
          </span>
        </div>
        <Icon className="w-4 h-4 text-ink-30 group-hover:text-ink transition-colors" strokeWidth={1.5} data-id="element-24" />
      </div>
      <div className="font-serif font-bold text-2xl text-primary leading-tight mb-2" data-id="element-25">
        {award.title}
      </div>
      <div className="text-sm text-ink-50 tabular-nums" data-id="element-26">{award.metric}</div>
    </motion.button>;
}